// ============================================================================
// Cognito Service - Direct AWS Cognito Authentication
// EduPath Africa v2.0
// ============================================================================

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// ============================================================================
// Configuration
// ============================================================================

const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID || 'eu-west-1_Gdrd8H0m9';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || '5h8smu9c5rj2imog04t59avpco';

const poolData = {
  UserPoolId: USER_POOL_ID,
  ClientId: CLIENT_ID,
};

const userPool = new CognitoUserPool(poolData);

// ============================================================================
// Types
// ============================================================================

export interface CognitoAuthResult {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

export interface RegisterParams {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  grade?: number;
}

// ============================================================================
// Authentication Functions
// ============================================================================

/**
 * Sign in with email and password
 */
export function signIn(email: string, password: string): Promise<CognitoAuthResult> {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        resolve({
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken(),
        });
      },
      onFailure: (err) => {
        console.error('Cognito sign in error:', err);
        reject(new Error(mapCognitoError(err)));
      },
      newPasswordRequired: () => {
        reject(new Error('Password change required. Please contact support.'));
      },
    });
  });
}

/**
 * Register a new user
 */
export function signUp(params: RegisterParams): Promise<{ userSub: string }> {
  return new Promise((resolve, reject) => {
    const attributeList: CognitoUserAttribute[] = [
      new CognitoUserAttribute({ Name: 'email', Value: params.email }),
    ];

    // Only add phone if provided (must be in E.164 format: +254...)
    if (params.phone && params.phone.startsWith('+')) {
      attributeList.push(
        new CognitoUserAttribute({ Name: 'phone_number', Value: params.phone })
      );
    }
    
    // Add name attributes (these are standard Cognito attributes)
    if (params.firstName) {
      attributeList.push(
        new CognitoUserAttribute({ Name: 'given_name', Value: params.firstName })
      );
    }
    if (params.lastName) {
      attributeList.push(
        new CognitoUserAttribute({ Name: 'family_name', Value: params.lastName })
      );
    }

    userPool.signUp(
      params.email,
      params.password,
      attributeList,
      [],
      (err, result) => {
        if (err) {
          console.error('Cognito sign up error:', err);
          reject(new Error(mapCognitoError(err)));
          return;
        }
        resolve({ userSub: result?.userSub || '' });
      }
    );
  });
}

/**
 * Confirm registration with verification code
 */
export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        console.error('Cognito confirm error:', err);
        reject(new Error(mapCognitoError(err)));
        return;
      }
      resolve();
    });
  });
}

/**
 * Resend verification code
 */
export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        console.error('Cognito resend code error:', err);
        reject(new Error(mapCognitoError(err)));
        return;
      }
      resolve();
    });
  });
}

/**
 * Sign out current user
 */
export function signOut(): void {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

/**
 * Get current authenticated session
 */
export function getCurrentSession(): Promise<CognitoAuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }

      resolve({
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
      });
    });
  });
}

/**
 * Refresh the session
 */
export function refreshSession(): Promise<CognitoAuthResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(new Error('Session expired'));
        return;
      }

      const refreshToken = session.getRefreshToken();
      
      cognitoUser.refreshSession(refreshToken, (refreshErr, newSession) => {
        if (refreshErr) {
          reject(new Error(mapCognitoError(refreshErr)));
          return;
        }

        resolve({
          accessToken: newSession.getAccessToken().getJwtToken(),
          idToken: newSession.getIdToken().getJwtToken(),
          refreshToken: newSession.getRefreshToken().getToken(),
        });
      });
    });
  });
}

/**
 * Initiate forgot password flow
 */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        console.error('Cognito forgot password error:', err);
        reject(new Error(mapCognitoError(err)));
      },
    });
  });
}

/**
 * Confirm new password with code
 */
export function confirmForgotPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        console.error('Cognito confirm password error:', err);
        reject(new Error(mapCognitoError(err)));
      },
    });
  });
}

/**
 * Change password for the currently authenticated user.
 * Requires the user to be signed in (has a valid Cognito session).
 */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No authenticated user. Please sign in again.'));
      return;
    }

    // Must get session first to authenticate the change
    cognitoUser.getSession((sessionErr: Error | null, session: CognitoUserSession | null) => {
      if (sessionErr || !session || !session.isValid()) {
        reject(new Error('Session expired. Please sign in again.'));
        return;
      }

      cognitoUser.changePassword(oldPassword, newPassword, (err) => {
        if (err) {
          console.error('Cognito change password error:', err);
          reject(new Error(mapCognitoError(err)));
          return;
        }
        resolve();
      });
    });
  });
}

// ============================================================================
// Error Mapping
// ============================================================================

function mapCognitoError(err: any): string {
  const code = err?.code || err?.name || '';
  
  switch (code) {
    case 'UserNotFoundException':
      return 'No account found with this email address.';
    case 'NotAuthorizedException':
      return 'Incorrect email or password.';
    case 'UserNotConfirmedException':
      return 'Please verify your email address before signing in.';
    case 'UsernameExistsException':
      return 'An account with this email already exists.';
    case 'InvalidPasswordException':
      return 'Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, and numbers.';
    case 'CodeMismatchException':
      return 'Invalid verification code. Please check and try again.';
    case 'ExpiredCodeException':
      return 'Verification code has expired. Please request a new one.';
    case 'LimitExceededException':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'InvalidParameterException':
      return 'Invalid input. Please check your information and try again.';
    case 'NetworkError':
      return 'Network error. Please check your internet connection.';
    default:
      return err?.message || 'An unexpected error occurred. Please try again.';
  }
}

export const cognitoService = {
  signIn,
  signUp,
  confirmSignUp,
  resendConfirmationCode,
  signOut,
  getCurrentSession,
  refreshSession,
  forgotPassword,
  confirmForgotPassword,
  changePassword,
};

export default cognitoService;
